import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import axiosInstance, { MAPKEY } from "../../axiosInstance";
import getDecryptData from "../../helper/getDecryptData";

const initialSelectedAddress = {
  label: "Address",
  address: "",
  latitude: "",
  longitude: "",
  isDefault: true,
};

const MyLocation = () => {
  const navigate = useNavigate();
  const loggedInUser = getDecryptData();
  const userId = loggedInUser?._id || null;

  const [searchValue, setSearchValue] = useState("");
  const [label, setLabel] = useState("Address");
  const [searchResults, setSearchResults] = useState([]);
  const [currentLocation, setCurrentLocation] = useState("");
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const [selectedAddress, setSelectedAddress] = useState(initialSelectedAddress);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [editingAddressId, setEditingAddressId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const hasSelectedAddress = Boolean(searchValue.trim());

  const saveLocationToStorage = (addressData) => {
    localStorage.setItem("selectedLocation", addressData.address);
    localStorage.setItem("latitude", addressData.latitude || "");
    localStorage.setItem("longitude", addressData.longitude || "");
  };

  const resetForm = () => {
    setSearchValue("");
    setLabel("Address");
    setSelectedAddress(initialSelectedAddress);
    setEditingAddressId(null);
    setSearchResults([]);
  };

  const fetchSavedAddresses = async () => {
    if (!userId) return;

    try {
      const { data } = await axiosInstance.get(`/user-addresses/${userId}`);
      setSavedAddresses(data.addresses || []);
    } catch (error) {
      console.error("Error fetching saved addresses:", error);
    }
  };

  useEffect(() => {
    const storedLocation = localStorage.getItem("selectedLocation");
    const storedLatitude = localStorage.getItem("latitude");
    const storedLongitude = localStorage.getItem("longitude");

    if (storedLocation) {
      setSearchValue(storedLocation);
      setSelectedAddress({
        label: "Address",
        address: storedLocation,
        latitude: storedLatitude || "",
        longitude: storedLongitude || "",
        isDefault: true,
      });
    }

    fetchSavedAddresses();
  }, [userId]);

  useEffect(() => {
    const initializeAutocomplete = () => {
      const input = document.getElementById("location-input");
      if (!input || !window.google?.maps?.places) return;

      const autocomplete = new window.google.maps.places.Autocomplete(input);
      autocomplete.addListener("place_changed", () => {
        const place = autocomplete.getPlace();
        if (!place?.geometry?.location) return;

        const address = place.formatted_address || place.name || searchValue;
        const nextAddress = {
          label,
          address,
          latitude: place.geometry.location.lat(),
          longitude: place.geometry.location.lng(),
          isDefault: true,
        };

        setSearchValue(address);
        setSelectedAddress(nextAddress);
      });
    };

    const loadGoogleMaps = () => {
      if (window.google?.maps?.places) {
        initializeAutocomplete();
        return;
      }

      if (!document.querySelector(`script[src*="https://maps.googleapis.com/maps/api/js"]`)) {
        const script = document.createElement("script");
        script.src = `https://maps.googleapis.com/maps/api/js?key=${MAPKEY}&libraries=places`;
        script.async = true;
        script.defer = true;
        script.onload = initializeAutocomplete;
        document.body.appendChild(script);
      }
    };

    loadGoogleMaps();

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude: currentLat, longitude: currentLng } = position.coords;
          setLatitude(currentLat);
          setLongitude(currentLng);

          fetch(
            `https://maps.googleapis.com/maps/api/geocode/json?latlng=${currentLat},${currentLng}&key=${MAPKEY}`
          )
            .then((response) => response.json())
            .then((data) => {
              const address = data.results?.[0]?.formatted_address;
              if (address) {
                setCurrentLocation(address);
              }
            })
            .catch((error) => {
              console.error("Error fetching current location:", error);
            });
        },
        (error) => {
          console.error("Error getting user location:", error);
        }
      );
    }
  }, []);

  const saveAddress = async (addressData = selectedAddress) => {
    const nextAddress = {
      ...addressData,
      label: label || addressData.label || "Address",
      address: addressData.address || searchValue,
      latitude: addressData.latitude || "",
      longitude: addressData.longitude || "",
      isDefault: true,
    };

    if (!nextAddress.address) {
      toast.error("Please select an address");
      return;
    }

    saveLocationToStorage(nextAddress);

    if (!userId) {
      toast.success("Location saved");
      navigate("/");
      return;
    }

    try {
      setIsSaving(true);
      const request = editingAddressId
        ? axiosInstance.put(`/user-addresses/${userId}/${editingAddressId}`, nextAddress)
        : axiosInstance.post(`/user-addresses/${userId}`, nextAddress);

      const { data } = await toast.promise(request, {
        loading: editingAddressId ? "Updating address..." : "Saving address...",
        success: editingAddressId ? "Address updated" : "Address saved",
        error: "Failed to save address",
      });

      setSavedAddresses(data.addresses || []);
      resetForm();
    } catch (error) {
      console.error("Error saving address:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchValue(value);
    setSelectedAddress((prev) => ({ ...prev, address: value }));

    if (value.trim() === "" || !window.google?.maps?.places) {
      setSearchResults([]);
      return;
    }

    const service = new window.google.maps.places.AutocompleteService();
    service.getPlacePredictions({ input: value }, (predictions, status) => {
      if (status === window.google.maps.places.PlacesServiceStatus.OK) {
        setSearchResults(predictions || []);
      } else {
        setSearchResults([]);
      }
    });
  };

  const handleSearchResultClick = (result) => {
    const service = new window.google.maps.places.PlacesService(document.createElement("div"));

    service.getDetails(
      {
        placeId: result.place_id,
        fields: ["geometry", "formatted_address", "name"],
      },
      (place, status) => {
        if (status !== window.google.maps.places.PlacesServiceStatus.OK) {
          toast.error("Unable to fetch selected address");
          return;
        }

        const nextAddress = {
          label,
          address: place.formatted_address || result.description,
          latitude: place.geometry.location.lat(),
          longitude: place.geometry.location.lng(),
          isDefault: true,
        };

        setSearchValue(nextAddress.address);
        setSelectedAddress(nextAddress);
        setSearchResults([]);
      }
    );
  };

  const handleSaveCurrentLocation = () => {
    if (!currentLocation || !latitude || !longitude) {
      toast.error("Unable to fetch current location");
      return;
    }

    const nextAddress = {
      label,
      address: currentLocation,
      latitude,
      longitude,
      isDefault: true,
    };

    setSearchValue(currentLocation);
    setSelectedAddress(nextAddress);
    saveAddress(nextAddress);
  };

  const selectSavedAddress = (addressData) => {
    saveLocationToStorage(addressData);
    toast.success("Pickup address selected");
    navigate("/");
  };

  const editSavedAddress = (addressData) => {
    setEditingAddressId(addressData._id);
    setLabel(addressData.label || "Address");
    setSearchValue(addressData.address || "");
    setSelectedAddress({
      label: addressData.label || "Address",
      address: addressData.address || "",
      latitude: addressData.latitude || "",
      longitude: addressData.longitude || "",
      isDefault: true,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const deleteSavedAddress = async (addressId) => {
    if (!userId) return;

    try {
      const { data } = await toast.promise(
        axiosInstance.delete(`/user-addresses/${userId}/${addressId}`),
        {
          loading: "Deleting address...",
          success: "Address deleted",
          error: "Failed to delete address",
        }
      );
      setSavedAddresses(data.addresses || []);
      if (editingAddressId === addressId) {
        resetForm();
      }
    } catch (error) {
      console.error("Error deleting address:", error);
    }
  };

  return (
    <main className="location-app-shell">
      <style>{`
      .pac-container.pac-logo.hdpi{
      display:none !important;
      }
        .location-app-shell {
          min-height: 100vh;
          background: #f6f8fb;
          color: #111827;
          padding-bottom: 96px;
        }

        .location-app {
          width: min(100%, 520px);
          margin: 0 auto;
        }

        .location-top {
          position: sticky;
          top: 0;
          z-index: 20;
          background: rgba(246, 248, 251, 0.96);
          backdrop-filter: blur(12px);
          padding: 14px 16px 10px;
          border-bottom: 1px solid #e8edf3;
        }

        .location-title-row {
          display: grid;
          grid-template-columns: 42px 1fr 42px;
          align-items: center;
          gap: 8px;
          margin-bottom: 14px;
        }

        .location-icon-btn {
          width: 42px;
          height: 42px;
          border: 0;
          border-radius: 50%;
          background: #ffffff;
          color: #111827;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 8px 22px rgba(15, 23, 42, 0.08);
        }

        .location-title {
          text-align: center;
          min-width: 0;
        }

        .location-title h1 {
          font-size: 18px;
          line-height: 1.2;
          margin: 0;
          font-weight: 800;
        }

        .location-title p {
          margin: 3px 0 0;
          font-size: 12px;
          color: #6b7280;
        }

        .location-search-box {
          display: flex;
          align-items: center;
          gap: 10px;
          min-height: 54px;
          border-radius: 18px;
          background: #ffffff;
          padding: 0 12px;
          box-shadow: 0 12px 28px rgba(15, 23, 42, 0.08);
          border: 1px solid #eef2f7;
        }

        .location-search-box input {
          border: 0;
          outline: 0;
          min-width: 0;
          flex: 1;
          height: 52px;
          font-size: 15px;
          background: transparent;
          color: #111827;
        }

        .location-search-box input::placeholder {
          color: #9ca3af;
        }

        .location-clear {
          border: 0;
          background: #f3f4f6;
          color: #6b7280;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }

        .location-content {
          padding: 14px 16px 0;
        }

        .location-card {
          background: #ffffff;
          border: 1px solid #e9eef5;
          border-radius: 18px;
          box-shadow: 0 12px 30px rgba(15, 23, 42, 0.07);
        }

        .location-current {
          border: 0;
          width: 100%;
          text-align: left;
          padding: 14px;
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .location-current-icon {
          width: 42px;
          height: 42px;
          flex: 0 0 42px;
          border-radius: 14px;
          background: #e8f8ef;
          color: #159947;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
        }

        .location-section-title {
          margin: 22px 2px 10px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
        }

        .location-section-title h2 {
          font-size: 14px;
          line-height: 1.2;
          margin: 0;
          font-weight: 800;
          color: #111827;
        }

        .location-text-btn {
          border: 0;
          background: transparent;
          color: #b0833a;
          font-size: 13px;
          font-weight: 700;
          padding: 0;
        }

        .location-label-row {
          display: flex;
          gap: 8px;
          overflow-x: auto;
          padding: 2px 0 6px;
          scrollbar-width: none;
        }

        .location-label-row::-webkit-scrollbar {
          display: none;
        }

        .location-chip {
          border: 1px solid #e5e7eb;
          background: #ffffff;
          color: #374151;
          border-radius: 999px;
          padding: 8px 13px;
          font-size: 13px;
          font-weight: 700;
          white-space: nowrap;
        }

        .location-chip.active {
          background: #111827;
          color: #ffffff;
          border-color: #111827;
        }

        .location-label-input {
          width: 100%;
          border: 1px solid #e5e7eb;
          border-radius: 15px;
          background: #ffffff;
          height: 48px;
          padding: 0 14px;
          font-size: 14px;
          outline: 0;
        }

        .location-list {
          overflow: hidden;
        }

        .location-result,
        .location-saved {
          width: 100%;
          border: 0;
          background: #ffffff;
          text-align: left;
          padding: 14px;
          display: grid;
          grid-template-columns: 42px 1fr auto;
          gap: 12px;
          align-items: center;
          border-bottom: 1px solid #eef2f7;
        }

        .location-result:last-child,
        .location-saved:last-child {
          border-bottom: 0;
        }

        .location-pin {
          width: 42px;
          height: 42px;
          border-radius: 14px;
          background: #f1f5f9;
          color: #b0833a;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
        }

        .location-main-text {
          display: block;
          font-size: 14px;
          font-weight: 800;
          color: #111827;
          line-height: 1.25;
          overflow-wrap: anywhere;
        }

        .location-sub-text {
          display: -webkit-box;
          margin-top: 3px;
          color: #6b7280;
          font-size: 12px;
          line-height: 1.35;
          overflow: hidden;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
        }

        .location-badge {
          display: inline-flex;
          align-items: center;
          margin-left: 6px;
          border-radius: 999px;
          padding: 3px 7px;
          background: #e8f8ef;
          color: #147f3c;
          font-size: 10px;
          font-weight: 800;
        }

        .location-actions {
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .location-mini-btn {
          width: 34px;
          height: 34px;
          border: 0;
          border-radius: 50%;
          background: #f3f4f6;
          color: #374151;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex: 0 0 34px;
        }

        .location-mini-btn.primary {
          background: #b0833a;
          color: #ffffff;
        }

        .location-mini-btn.danger {
          background: #fee2e2;
          color: #dc2626;
        }

        .location-empty {
          padding: 24px 16px;
          text-align: center;
          color: #6b7280;
          font-size: 13px;
        }

        .location-bottom-bar {
          position: fixed;
          left: 50%;
          bottom: 0;
          transform: translateX(-50%);
          z-index: 30;
          width: min(100%, 520px);
          padding: 12px 16px calc(12px + env(safe-area-inset-bottom));
          background: rgba(246, 248, 251, 0.94);
          backdrop-filter: blur(12px);
          border-top: 1px solid #e8edf3;
        }

        .location-primary-btn {
          width: 100%;
          min-height: 54px;
          border: 0;
          border-radius: 17px;
          background: #b0833a;
          color: #ffffff;
          font-size: 15px;
          font-weight: 800;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          box-shadow: 0 14px 30px rgba(13, 110, 253, 0.28);
        }

        .location-primary-btn:disabled {
          opacity: 0.55;
          box-shadow: none;
        }

        .location-cancel-btn {
          margin-top: 9px;
          width: 100%;
          min-height: 42px;
          border: 0;
          border-radius: 14px;
          background: #ffffff;
          color: #374151;
          font-size: 14px;
          font-weight: 800;
        }

        @media (min-width: 576px) {
          .location-top,
          .location-content,
          .location-bottom-bar {
            padding-left: 20px;
            padding-right: 20px;
          }
        }
      `}</style>

      <div className="location-app">
        <div className="location-top">
          <div className="location-title-row">
            <Link to={"/"} className="location-icon-btn" aria-label="Back">
              <i className="ri-arrow-left-line fs-5"></i>
            </Link>

            <div className="location-title">
              <h1>{editingAddressId ? "Edit Address" : "Choose Location"}</h1>
              <p>{userId ? "Saved pickup address" : "Pickup location"}</p>
            </div>

            <button type="button" className="location-icon-btn" onClick={resetForm} aria-label="Clear">
              <i className="ri-refresh-line fs-5"></i>
            </button>
          </div>

          <div className="location-search-box">
            <i className="ri-search-line fs-5 text-primary"></i>
            <input
              type="text"
              id="location-input"
              placeholder="Search area, street, landmark"
              value={searchValue}
              onChange={handleSearchChange}
            />
            {searchValue !== "" && (
              <button type="button" className="location-clear" onClick={resetForm} aria-label="Clear search">
                <i className="ri-close-line fs-5"></i>
              </button>
            )}
          </div>
        </div>

        <div className="location-content">
          <button type="button" className="location-card location-current" onClick={handleSaveCurrentLocation}>
            <span className="location-current-icon">
              <i className="ri-crosshair-2-fill"></i>
            </span>
            <span className="min-w-0 flex-fill">
              <span className="location-main-text">Use current location</span>
              <span className="location-sub-text">
                {currentLocation || "Allow location permission for instant pickup"}
              </span>
            </span>
            <i className="ri-arrow-right-s-line fs-4 text-muted"></i>
          </button>

          {userId && (
            <>
              <div className="location-section-title">
                <h2>Address label</h2>
              </div>

              <div className="location-label-row">
                {["Home", "Office", "Hotel", "Other"].map((item) => (
                  <button
                    type="button"
                    key={item}
                    className={`location-chip ${label === item ? "active" : ""}`}
                    onClick={() => setLabel(item)}
                  >
                    {item}
                  </button>
                ))}
              </div>

              <input
                type="text"
                className="location-label-input"
                placeholder="Name this address"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
              />
            </>
          )}

          {searchResults.length > 0 && (
            <>
              <div className="location-section-title">
                <h2>Search Results</h2>
                <button type="button" onClick={resetForm} className="location-text-btn">
                  Clear
                </button>
              </div>

              <div className="location-card location-list">
                {searchResults.map((result) => (
                  <button
                    type="button"
                    className="location-result"
                    key={result.place_id}
                    onClick={() => handleSearchResultClick(result)}
                  >
                    <span className="location-pin">
                      <i className="ri-map-pin-2-fill"></i>
                    </span>
                    <span>
                      <span className="location-main-text">
                        {result.structured_formatting.main_text}
                      </span>
                      <span className="location-sub-text">
                        {result.structured_formatting.secondary_text}
                      </span>
                    </span>
                    <i className="ri-arrow-right-s-line fs-4 text-muted"></i>
                  </button>
                ))}
              </div>
            </>
          )}

          {userId && (
            <>
              <div className="location-section-title">
                <h2>Saved Addresses</h2>
              </div>

              <div className="location-card location-list">
                {savedAddresses.length === 0 ? (
                  <div className="location-empty">
                    No saved address yet
                  </div>
                ) : (
                  savedAddresses.map((addressData) => (
                    <div className="location-saved" key={addressData._id}>
                      <span className="location-pin">
                        <i className="ri-map-pin-2-fill"></i>
                      </span>
                      <span>
                        <span className="location-main-text">
                          {addressData.label || "Address"}
                          {addressData.isDefault && <span className="location-badge">Default</span>}
                        </span>
                        <span className="location-sub-text">{addressData.address}</span>
                      </span>
                      <span className="location-actions">
                        <button
                          type="button"
                          className="location-mini-btn primary"
                          onClick={() => selectSavedAddress(addressData)}
                          aria-label="Use address"
                        >
                          <i className="ri-check-line"></i>
                        </button>
                        <button
                          type="button"
                          className="location-mini-btn"
                          onClick={() => editSavedAddress(addressData)}
                          aria-label="Edit address"
                        >
                          <i className="ri-pencil-line"></i>
                        </button>
                        <button
                          type="button"
                          className="location-mini-btn danger"
                          onClick={() => deleteSavedAddress(addressData._id)}
                          aria-label="Delete address"
                        >
                          <i className="ri-delete-bin-line"></i>
                        </button>
                      </span>
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <div className="location-bottom-bar">
        <button
          type="button"
          className="location-primary-btn"
          onClick={() => saveAddress()}
          disabled={isSaving || !hasSelectedAddress}
        >
          <i className={editingAddressId ? "ri-save-line" : "ri-map-pin-add-line"}></i>
          {editingAddressId ? "Update Address" : userId ? "Save Pickup Address" : "Use This Location"}
        </button>
        {editingAddressId && (
          <button type="button" className="location-cancel-btn" onClick={resetForm}>
            Cancel Editing
          </button>
        )}
      </div>
    </main>
  );
};

export default MyLocation;
